'use client';

import { Search, X } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../utils/axiosInstance';
import { ModifyCard } from '../../components/modify/ModifyCard';
import { Skeleton } from '@/components/ui/skeleton';

export const AddItemsModal = ({ isOpen, onClose, onAddItem }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [productCategoryMap, setProductCategoryMap] = useState({});

  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user } = useUser();

  const sessionToken = useMemo(() => localStorage.getItem('sessionToken'), []);
  const refreshToken = useMemo(() => localStorage.getItem('refreshToken'), []);

  const headers = useMemo(
    () => ({
      sessionToken,
      refreshToken,
    }),
    [sessionToken, refreshToken]
  );

  // First fetch categories
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const { data } = await axiosInstance.get(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products/categories`,
        {
          headers,
        }
      );

      let vendorCategories;
      if (user?.userType === 'vendor') {
        vendorCategories = user?.vendorCategories || [];
      } else {
        const selectedVendor = JSON.parse(localStorage.getItem('selectedVendor'));
        vendorCategories = selectedVendor?.vendorCategories || [];
      }

      const syncedCategories = data.filter((category) => vendorCategories.includes(category.name));
      setCategories(syncedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Then fetch products using category IDs
  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const selectedVendorData = JSON.parse(localStorage.getItem('selectedVendor'));

      if (user?.userType === 'user' && !selectedVendorData) {
        return;
      }

      let vendorId;
      if (user?.userType === 'user') {
        vendorId = selectedVendorData.vendorId;
      } else {
        vendorId = user?.id;
      }

      // Skip if no categories
      if (!categories || categories.length === 0) {
        setLoading(false);
        return;
      }

      // Create a map to store which category each product belongs to
      const categoryMap = {};

      const productPromises = categories.map((category) =>
        axiosInstance
          .get(
            `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products?category=${category.id}`,
            {
              headers: {
                'X-Vendor-Id': vendorId,
                sessionToken: localStorage.getItem('sessionToken'),
                refreshToken: localStorage.getItem('refreshToken'),
              },
            }
          )
          .then((response) => {
            // For each product in this category, add an entry to the map
            response.data.forEach((product) => {
              categoryMap[product.productId] = category.id;
            });
            return response;
          })
      );

      // Use Promise.allSettled instead of Promise.all to handle individual failures
      const results = await Promise.allSettled(productPromises);

      // Filter for successful responses only
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value.data);

      // Flatten the array of product arrays
      const products = successfulResponses.flat();

      // Log any failed requests
      const failedRequests = results.filter((result) => result.status === 'rejected');
      if (failedRequests.length > 0) {
        console.warn(
          `${failedRequests.length} category requests failed, but continuing with available products`
        );
      }

      // Store the category map
      setProductCategoryMap(categoryMap);
      setAllProducts(products);
      setFilteredProducts(products);
    } catch (error) {
      console.error('Error in fetchAllProducts:', error);
      // Only logout on specific auth errors
      if (
        error.status === 401 ||
        error?.response?.data === 'Unauthorized: Invalid or missing token.'
      ) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(allProducts);
    } else {
      const filtered = allProducts.filter((product) => {
        const categoryId = productCategoryMap[product.productId];
        const category = categories.find((cat) => cat.id === categoryId);
        const categoryName = category ? category.name : '';

        return (
          product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (categoryName && categoryName.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
      setFilteredProducts(filtered);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    filterProducts();
  };

  const handleProductSelect = (product, quantity = 1) => {
    // Create an item object that matches the structure expected by OrderDetails
    const newItem = {
      productId: product.productId,
      productName: product.productName,
      productImageUrl: product.productImageUrl,
      mrp: product.productVariants[0].mrp || product.mrp,
      netPrice: product.productVariants[0].netPrice || product.netPrice,
      updatedNetPrice: product.productVariants[0].netPrice || product.netPrice,
      quantity: quantity,
      variant: product.productVariants[0].variant,
      updatedVariant: product.productVariants[0].variant,
      unit: product.productVariants[0].unit,
      inventoryId: product.productVariants[0].inventoryId,
      total: (product.productVariants[0].netPrice || product.netPrice) * quantity,
      edited: true, // Mark as edited since it's a new addition
    };

    // Pass the new item to the parent component
    onAddItem(newItem);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (categories.length > 0 && user) {
      fetchAllProducts();
    }
  }, [categories, user]);

  useEffect(() => {
    if (allProducts.length > 0) {
      filterProducts();
    }
  }, [searchTerm, allProducts]);

  const getCategoryNameById = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  return (
    <div className="mx-auto fixed w-full h-full flex justify-center items-center bg-black/30 z-50 top-0 left-0">
      <div className="max-w-md w-full overflow-auto md:max-w-sm h-[90%] mx-auto rounded-md relative bg-white">
        <header className="flex items-center gap-4 bg-white border-b px-4 py-3 sticky top-0 z-10">
          <X onClick={onClose} className="h-6 w-6 cursor-pointer" />
          <form onSubmit={handleSearch} className="flex-1 flex items-center">
            <input
              type="search"
              placeholder="Search products..."
              value={searchTerm}
              autoFocus
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 py-1 px-3 focus:outline-none border-2 border-gray-200 focus:border-gray-500 rounded-md"
            />
            <button type="submit" className="ml-2">
              <Search className="h-5 w-5" />
            </button>
          </form>
        </header>
        <section className="px-4 py-3">
          {(loading || loadingCategories) && (
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-lg overflow-hidden border">
                  <Skeleton className="h-32 w-full" />
                  <div className="p-3 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && !loadingCategories && (
            <div className="grid grid-cols-2 gap-4">
              {filteredProducts.map((product) => (
                <ModifyCard
                  key={product.productId}
                  product={product}
                  onSelect={handleProductSelect}
                  category={getCategoryNameById(productCategoryMap[product.productId])}
                />
              ))}
            </div>
          )}
          {!loading && !loadingCategories && filteredProducts.length === 0 && (
            <div className="text-center mt-8 py-10">
              <div className="bg-gray-100 inline-flex rounded-full p-3 mb-4">
                <Search className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-gray-700 font-medium">No products found</p>
              <p className="text-sm text-gray-500 mt-2">Try adjusting your search</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
